import Badge from '@axa-japan/design-system-react/Badge';
import Checkbox from '@axa-japan/design-system-react/Checkbox';
import Text from '@axa-japan/design-system-react/Text';
import { Document, DOCUMENT_STATUS } from '../../types/session';
import { ValueOf } from '../../utils/fileUtils';
import { renderCustomTags } from '../../utils/renderTags';

const DocumentStatusBadge = ({ status }: { status: ValueOf<typeof DOCUMENT_STATUS> }) => {
  if (status === DOCUMENT_STATUS.urlGenerated) return <Badge className="text-center font-bold" variant="success" text="QRコード発行済" />;
  if (status === DOCUMENT_STATUS.smsSent) return <Badge className="text-center font-bold" variant="success" text="SMS送信済" />;
  if (status === DOCUMENT_STATUS.uploadCompleted) return <Badge className="text-center font-bold" variant="success" text="送信済み" />;
  if (status === DOCUMENT_STATUS.uploadFailed) return <Badge className="text-center font-bold" variant="danger" text="送信エラー" />;
  return <Badge className="text-center font-bold" variant="neutral" text="未送信" />;
};

type DocumentTableProps = {
  dataTable: Document[];
  onSelectRow?: (rows: string[]) => void;
  selectedRows: string[];
  readonly?: boolean;
};

const DocumentTable = ({ dataTable, onSelectRow, readonly, selectedRows }: DocumentTableProps) => {
  const handleRowClick = (checked: boolean, dataId: string) => {
    // remove from list when unchecked
    const tmpDataList = selectedRows.filter((id) => id !== dataId);
    if (checked) tmpDataList.push(dataId);
    if (onSelectRow) onSelectRow([...tmpDataList]);
  };

  const handleClickAll = () => {
    if (selectedRows.length !== dataTable.length) {
      if (onSelectRow) onSelectRow(dataTable.map((item) => item.id));
    } else {
      if (onSelectRow) onSelectRow([]);
    }
  };

  return (
    <div className="relative overflow-x-auto">
      <table className="w-full text-sm text-left rtl:text-right">
        {!readonly && (
          <colgroup>
            <col className="w-1/12" />
            <col className="w-8/12 md:w-7/12" />
            <col className="w-2/12" />
            <col className="w-1/12 md:w-2/12" />
          </colgroup>
        )}

        <thead className="text-xs text-gray-700 uppercase bg-wildSand">
          <tr>
            {!readonly && (
              <th scope="col" className="px-1 py-3 text-center">
                <div onClick={handleClickAll} className="inline-block">
                  <Checkbox name={'CheckAll'} data-testid="CheckAll" label="" checked={selectedRows.length === dataTable.length} value={'all'} />
                </div>
              </th>
            )}
            <th scope="col" className={`pr-6 py-3 ${readonly && 'pl-6'}`}>
              <Text weight="bold">書類名</Text>
            </th>
            {!readonly && (
              <th scope="col" className="px-6 py-3 text-center">
                <Text align="center" weight="bold">
                  ステータス
                </Text>
              </th>
            )}
            <th scope="col" className="px-6 py-3 text-center">
              <Text align="center" weight="bold">
                原本送付
              </Text>
            </th>
          </tr>
        </thead>
        <tbody>
          {dataTable.map((data, key) => {
            return (
              <tr key={key} className="bg-white border-b border-lightGray">
                {!readonly && (
                  <td className="py-4 px-1 text-center">
                    <div className="inline-block">
                      <Checkbox
                        onCheckedChange={(dt) => {
                          let isChecked;
                          if (typeof dt.checked === 'string' && dt.checked === 'indeterminate') {
                            isChecked = false;
                          } else {
                            isChecked = dt.checked;
                          }
                          handleRowClick(isChecked, data.id);
                        }}
                        name={`CheckBox-${data.id}`}
                        data-testid={`CheckBox-${data.id}`}
                        checked={selectedRows.some((id) => id === data.id)}
                        label=""
                        value={`${data.id}`}
                      />
                    </div>
                  </td>
                )}

                <td className={`pr-6 py-4 content-center ${readonly && 'pl-6'}`}>
                  <Text weight="bold">{data.name}</Text>
                  {renderCustomTags(data.guidelines)}
                </td>
                {!readonly && (
                  <td className="px-6 py-4 text-center">
                    <DocumentStatusBadge status={data.uploadStage === 'CUSTOMER' ? data.documentStatus : data.documentStatus} />
                  </td>
                )}

                <td className="px-6 py-4 text-center">
                  {data.physicalCopyRequired ? (
                    <Text align="center" weight="bold" color="danger">
                      必要
                    </Text>
                  ) : (
                    <Text align="center" weight="bold">
                      不要
                    </Text>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default DocumentTable;
